import { ConfigAreaDto, ConfigAreaType, ReeferPlugDirection } from '@yard/app/api'

export const NODE_HEADER_GAP = 40
export const CONTAINER_DETAILS_WIDTH = 200
export const CONTAINER_DETAILS_HEIGHT = 145
export const LAYOUT_OBJECT_HEIGHT = 500
export const LAYOUT_OBJECT_WIDTH = 500
export const BERTH_HEIGHT = 50
export const RAIL_TRACK_HEIGHT = 50

export const GATE_HEIGHT = 160
export const GATE_WIDTH = 500
export const GATE_ROAD_NODE_MARGIN = 8

export const HOLDING_POINT_HEIGHT = 160
export const HOLDING_POINT_WIDTH = 300

export const HANDOVER_AREA_HEIGHT = 200
export const HANDOVER_AREA_WIDTH = 300

export const YARD_DRAWERS_WIDTH = 400
export const YARD_DRAWER_HEADER_HEIGHT = 65
export const SIDEBAR_WIDTH = 80

export const PIXELS_PER_METER = 16.5
export const GEO_SCALE = 1.433
export const CONTAINER_20FT_WIDTH_IN_METERS = 6.06
export const CONTAINER_20FT_HEIGHT_IN_METERS = 2.59

export enum ContainerViewMode {
  STACK,
  BAY,
  ROW,
}

export const generateSlotKey = (bay: number, row: number): string => {
  return `${bay}-${row}`
}

export const metersToPixels = (meters: number): number => {
  return meters * PIXELS_PER_METER
}

export const BAY_WIDTH = metersToPixels(CONTAINER_20FT_WIDTH_IN_METERS)
export const ROW_HEIGHT = metersToPixels(CONTAINER_20FT_HEIGHT_IN_METERS)

/**
 * File-private. The fixed visual width of a reefer plug rail column.
 * Render sites consume this only via `BayGap.widthPx`.
 */
const PLUG_GAP_WIDTH = 16

/**
 * Discriminated descriptor of a single bay-axis gap. `edge` is 0-based on the
 * left of bay 1 and `maxBays`-based on the right of the last bay; `edge = N`
 * for `1 <= N < maxBays` represents the gap between bay N and bay N+1.
 */
export type BayGap =
  | {
      edge: number
      widthPx: number
      kind: 'reefer'
      hasFore: boolean
      hasAft: boolean
    }
  | {
      edge: number
      widthPx: number
      kind: 'space-between'
      widthInMeters: number
    }

/**
 * Single producer for every bay-axis gap. Reefer plugs and SpaceBetweenBays
 * config areas both feed the same `BayGap[]` consumed by every render site
 * and the access-point bar resolver.
 *
 * Reefer plug rules:
 *   - Aft plug on bay N → gap at `edge = N` (or `edge = maxBays` for the last bay).
 *   - Fore plug on bay N (N > 1) → gap at `edge = N - 1`.
 *   - Fore plug on bay 1 → gap at `edge = 0` (leading).
 *   - Aft on bay N + Fore on bay N+1 collapse to a single entry with both flags.
 *
 * SpaceBetweenBays rules:
 *   - Each `SpaceBetweenBays` config area anchored on bay N produces a
 *     `kind: 'space-between'` entry at `edge = N` with `widthPx = widthInMeters * PIXELS_PER_METER`.
 *
 * Defensive: if both reefer and SBB occupy the same edge (the UI prevents this
 * but a direct API caller can produce it), the SBB entry wins because its
 * width is planner-supplied and meaningful.
 *
 * Returned entries are sorted by `edge` ascending.
 */
export const buildBayGaps = (
  configAreas: ConfigAreaDto[] | undefined,
  maxBays: number,
): BayGap[] => {
  const reeferByEdge = new Map<number, { hasFore: boolean; hasAft: boolean }>()
  const sbbByEdge = new Map<number, { widthInMeters: number }>()

  configAreas?.forEach(area => {
    if (area.type === ConfigAreaType.ReeferPlugs) {
      if (area.reeferPlugDirection === ReeferPlugDirection.Aft) {
        // Aft on bay N → edge N (covers trailing edge of last bay → edge maxBays)
        const edge = area.minBay
        const existing = reeferByEdge.get(edge) ?? { hasFore: false, hasAft: false }
        reeferByEdge.set(edge, { ...existing, hasAft: true })
      } else if (area.reeferPlugDirection === ReeferPlugDirection.Fore) {
        // Fore on bay N → edge N-1 (Fore on bay 1 → leading edge 0)
        const edge = area.minBay - 1
        const existing = reeferByEdge.get(edge) ?? { hasFore: false, hasAft: false }
        reeferByEdge.set(edge, { ...existing, hasFore: true })
      }
    } else if (area.type === ConfigAreaType.SpaceBetweenBays) {
      const widthInMeters = area.widthInMeters ?? 0
      sbbByEdge.set(area.minBay, { widthInMeters })
    }
  })

  const allEdges = new Set<number>([...reeferByEdge.keys(), ...sbbByEdge.keys()])
  const gaps: BayGap[] = []
  allEdges.forEach(edge => {
    const sbb = sbbByEdge.get(edge)
    if (sbb) {
      gaps.push({
        edge,
        widthPx: sbb.widthInMeters * PIXELS_PER_METER,
        kind: 'space-between',
        widthInMeters: sbb.widthInMeters,
      })
      return
    }
    const reefer = reeferByEdge.get(edge)
    if (reefer) {
      gaps.push({
        edge,
        widthPx: PLUG_GAP_WIDTH,
        kind: 'reefer',
        hasFore: reefer.hasFore,
        hasAft: reefer.hasAft,
      })
    }
  })

  gaps.sort((a, b) => a.edge - b.edge)
  // maxBays is part of the public contract for parity with the old buildGapAfterBay
  // signature; we don't currently filter by it but it scopes the producer to a block.
  void maxBays
  return gaps
}

/**
 * Builds a CSS grid-template-columns string with one bay column per bay and a
 * gap column inserted at every edge present in `bayGaps`.
 *
 * Edge 0 produces a leading column; edge N (1 <= N < maxBays) produces a
 * column after bay N; edge maxBays produces a trailing column.
 */
export const buildGridTemplateColumns = (maxBays: number, bayGaps: BayGap[]): string => {
  const byEdge = new Map<number, BayGap>()
  bayGaps.forEach(g => byEdge.set(g.edge, g))

  const parts: string[] = []
  if (byEdge.has(0)) parts.push(`${byEdge.get(0)!.widthPx}px`)
  for (let bay = 1; bay <= maxBays; bay++) {
    parts.push(`${BAY_WIDTH}px`)
    if (byEdge.has(bay)) parts.push(`${byEdge.get(bay)!.widthPx}px`)
  }
  return parts.join(' ')
}

/**
 * Sums `widthPx` for every BayGap whose `edge < bay`. Equivalent to the total
 * pixel offset of bay N's column from the start of the bay-axis grid (excluding
 * the bays themselves).
 */
export const gapPxBefore = (bay: number, bayGaps: BayGap[]): number =>
  bayGaps.filter(g => g.edge < bay).reduce((sum, g) => sum + g.widthPx, 0)

/**
 * Sums `widthPx` for every BayGap whose `edge` falls strictly between
 * `start` (inclusive) and `start + span - 1` (exclusive). Used to widen
 * a 40ft container's pixel span across an intervening gap.
 */
export const baySpanExtraPx = (start: number, span: number, bayGaps: BayGap[]): number =>
  bayGaps
    .filter(g => g.edge >= start && g.edge < start + span - 1)
    .reduce((sum, g) => sum + g.widthPx, 0)

/**
 * Counts BayGap entries whose `edge` falls between `start` (inclusive) and
 * `start + span - 1` (exclusive). Used for CSS grid-column-span math where
 * each gap contributes one extra grid column regardless of its pixel width.
 */
export const baySpanExtraGridColumns = (start: number, span: number, bayGaps: BayGap[]): number =>
  bayGaps.filter(g => g.edge >= start && g.edge < start + span - 1).length

/** Returns the BayGap at the given edge, or undefined if no gap occupies it. */
export const edgeAt = (bayGaps: BayGap[], edge: number): BayGap | undefined =>
  bayGaps.find(g => g.edge === edge)
